import { useState, useEffect, useMemo, useCallback, type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { usePowerSyncQuery, usePowerSyncDb } from '../../adapters/powersync';
import {
  qFrontDeskFieldSettings,
  qPatientDuplicateByMobile,
  type FrontDeskFieldSettingRow,
  type PatientDuplicateRow,
} from '@helios/data-access';
import { DEFAULT_FRONTDESK_PATIENT_FIELDS } from '@helios/fixtures';

// ─── Types ───

export interface QuickPatientResult {
  id: string;
  name: string;
}

// ─── Styles ───

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  lineHeight: 'var(--line-height-sm)',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  display: 'block',
  marginBottom: 'var(--sp-2)',
};

const inputStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-base)',
  lineHeight: 'var(--line-height-base)',
  color: 'var(--input-text)',
  fontFamily: 'inherit',
  boxSizing: 'border-box' as const,
  width: '100%',
};

const errorTextStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: '#dc2626',
  marginTop: 'var(--sp-1)',
};

// ─── Field config helper ───

interface FieldConfig {
  enabled: boolean;
  required: boolean;
  locked: boolean;
}

function getFieldConfig(fieldKey: string, fieldRows: FrontDeskFieldSettingRow[]): FieldConfig {
  const row = fieldRows.find((r) => r.field_key === fieldKey);
  if (row) {
    return {
      enabled: row.enabled == 1,
      required: row.required == 1,
      locked: row.locked == 1,
    };
  }
  const def = DEFAULT_FRONTDESK_PATIENT_FIELDS.find((f) => f.field_key === fieldKey);
  return {
    enabled: def?.enabled ?? false,
    required: def?.required ?? false,
    locked: def?.locked ?? false,
  };
}

// ─── Validation ───

interface ValidationErrors {
  name?: string;
  mobileNumber?: string;
  email?: string;
  dateOfBirth?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeMobile(raw: string): string {
  return raw.replace(/[\s\-()]/g, '');
}

function validate(
  name: string,
  mobileNumber: string,
  email: string,
  dateOfBirth: string,
  fields: Record<string, FieldConfig>,
): ValidationErrors {
  const errors: ValidationErrors = {};

  if (name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters';
  }

  if (fields.mobile_number.required && !normalizeMobile(mobileNumber)) {
    errors.mobileNumber = 'Mobile number is required';
  }

  if (email.trim()) {
    if (!EMAIL_RE.test(email.trim())) {
      errors.email = 'Invalid email format';
    }
  } else if (fields.email.required) {
    errors.email = 'Email is required';
  }

  if (dateOfBirth.trim()) {
    const d = new Date(dateOfBirth);
    if (isNaN(d.getTime())) {
      errors.dateOfBirth = 'Invalid date';
    } else if (d > new Date()) {
      errors.dateOfBirth = 'Date of birth cannot be in the future';
    }
  } else if (fields.date_of_birth.required) {
    errors.dateOfBirth = 'Date of birth is required';
  }

  return errors;
}

// ─── Component ───

interface QuickPatientDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  onCreated: (patient: QuickPatientResult) => void;
}

export function QuickPatientDrawer({
  open,
  onOpenChange,
  brandTokens,
  onCreated,
}: QuickPatientDrawerProps) {
  const { db } = usePowerSyncDb();

  const { data: fieldRows = [] } =
    usePowerSyncQuery<FrontDeskFieldSettingRow>(qFrontDeskFieldSettings());

  const fields = useMemo(
    () => ({
      patient_name: getFieldConfig('patient_name', fieldRows),
      mobile_country_code: getFieldConfig('mobile_country_code', fieldRows),
      mobile_number: getFieldConfig('mobile_number', fieldRows),
      email: getFieldConfig('email', fieldRows),
      date_of_birth: getFieldConfig('date_of_birth', fieldRows),
      gender: getFieldConfig('gender', fieldRows),
    }),
    [fieldRows],
  );

  // Form state
  const [name, setName] = useState('');
  const [mobileCountryCode, setMobileCountryCode] = useState('+63');
  const [mobileNumber, setMobileNumber] = useState('');
  const [email, setEmail] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [saving, setSaving] = useState(false);

  // Duplicate check state
  const [dupCheckMobile, setDupCheckMobile] = useState('');
  const [dupCheckCode, setDupCheckCode] = useState('');
  const { data: duplicates = [] } = usePowerSyncQuery<PatientDuplicateRow>(
    qPatientDuplicateByMobile(dupCheckCode, dupCheckMobile),
  );
  const [showDupWarning, setShowDupWarning] = useState(false);

  // Reset form on open
  useEffect(() => {
    if (open) {
      setName('');
      setMobileCountryCode('+63');
      setMobileNumber('');
      setEmail('');
      setDateOfBirth('');
      setGender('');
      setToast('');
      setErrors({});
      setSaving(false);
      setShowDupWarning(false);
      setDupCheckMobile('');
      setDupCheckCode('');
    }
  }, [open]);

  const persistPatient = useCallback(async () => {
    if (!db) return;
    setSaving(true);
    try {
      const patientId = crypto.randomUUID();
      const contactId = crypto.randomUUID();
      const now = new Date().toISOString();
      const patientNumber = `QP-${Date.now()}`;
      const normalizedMobile = normalizeMobile(mobileNumber);

      await db.execute(
        `INSERT INTO patients (id, patient_number, full_name, birth_date, sex, status, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [patientId, patientNumber, name.trim(), dateOfBirth || null, gender || null, 'active', now],
      );

      await db.execute(
        `INSERT INTO patient_contacts (id, patient_id, mobile_country_code, mobile_number, email)
         VALUES (?, ?, ?, ?, ?)`,
        [contactId, patientId, mobileCountryCode, normalizedMobile || null, email.trim() || null],
      );

      setToast(`Patient "${name.trim()}" created`);
      setToastType('success');
      setTimeout(() => {
        onCreated({ id: patientId, name: name.trim() });
        onOpenChange(false);
      }, 600);
    } catch (err) {
      console.error('Quick Patient create failed:', err);
      setToast('Failed to create patient. Please try again.');
      setToastType('error');
    } finally {
      setSaving(false);
    }
  }, [
    db,
    name,
    mobileCountryCode,
    mobileNumber,
    email,
    dateOfBirth,
    gender,
    onCreated,
    onOpenChange,
  ]);

  const handleCreate = useCallback(() => {
    // Client-side validation
    const validationErrors = validate(name, mobileNumber, email, dateOfBirth, fields);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    // Duplicate check by mobile
    const normalizedMobile = normalizeMobile(mobileNumber);
    if (normalizedMobile && mobileCountryCode) {
      setDupCheckCode(mobileCountryCode);
      setDupCheckMobile(normalizedMobile);
      // We need to wait for the query to update — use a flag
      // If duplicates are already loaded for this combo, check immediately
      if (
        dupCheckCode === mobileCountryCode &&
        dupCheckMobile === normalizedMobile &&
        duplicates.length > 0
      ) {
        setShowDupWarning(true);
        return;
      }
      // Set the query params and schedule a check after render
      setDupCheckCode(mobileCountryCode);
      setDupCheckMobile(normalizedMobile);
    }

    // No mobile to check, or no duplicates — persist directly
    persistPatient();
  }, [
    name,
    mobileNumber,
    email,
    dateOfBirth,
    gender,
    fields,
    mobileCountryCode,
    dupCheckCode,
    dupCheckMobile,
    duplicates,
    persistPatient,
  ]);

  // When duplicate query results update after setting dupCheck params, show warning if matches found
  useEffect(() => {
    if (
      dupCheckMobile &&
      dupCheckCode &&
      duplicates.length > 0 &&
      !showDupWarning &&
      saving === false
    ) {
      setShowDupWarning(true);
    } else if (
      dupCheckMobile &&
      dupCheckCode &&
      duplicates.length === 0 &&
      !showDupWarning &&
      saving === false &&
      errors &&
      Object.keys(errors).length === 0 &&
      name.trim().length >= 2
    ) {
      // Query returned but no duplicates — safe to persist
      // Only auto-persist if we just set the dup check (not on initial render)
      // We use a simple guard: dupCheckMobile is only set when user clicks Create
    }
  }, [duplicates, dupCheckMobile, dupCheckCode, showDupWarning, saving, errors, name]);

  const handleUseExisting = (dup: PatientDuplicateRow) => {
    setShowDupWarning(false);
    onCreated({ id: dup.id, name: dup.full_name ?? '' });
    onOpenChange(false);
  };

  const handleCreateAnyway = () => {
    setShowDupWarning(false);
    persistPatient();
  };

  const requiredMark = <span style={{ color: '#dc2626' }}>*</span>;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="New Patient"
      description="Create a new patient record"
      portalStyle={brandTokens as CSSProperties}
      footer={
        !showDupWarning ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="gradient" size="lg" onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating...' : 'Create Patient'}
            </Button>
          </div>
        ) : undefined
      }
    >
      <div
        style={{
          padding: 'var(--sp-6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5)',
        }}
      >
        {/* Toast */}
        {toast && (
          <div
            style={{
              padding: 'var(--sp-3) var(--sp-4)',
              borderRadius: 'var(--radius-md)',
              background: toastType === 'success' ? '#dcfce7' : '#fef2f2',
              color: toastType === 'success' ? '#15803d' : '#dc2626',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            {toast}
          </div>
        )}

        {/* Duplicate Warning Modal */}
        {showDupWarning && (
          <div
            style={{
              padding: 'var(--sp-4)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid #fbbf24',
              background: '#fffbeb',
            }}
          >
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: '#92400e',
                marginBottom: 'var(--sp-3)',
              }}
            >
              Possible duplicate found
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                color: '#78350f',
                marginBottom: 'var(--sp-3)',
              }}
            >
              A patient with the same mobile number already exists:
            </div>

            {duplicates.map((dup) => (
              <div
                key={dup.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--sp-2) var(--sp-3)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'white',
                  border: '1px solid var(--border)',
                  marginBottom: 'var(--sp-2)',
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                    }}
                  >
                    {dup.full_name}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    {dup.mobile_country_code} {dup.mobile_number}
                    {dup.patient_number ? ` · ${dup.patient_number}` : ''}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleUseExisting(dup)}>
                  Use existing
                </Button>
              </div>
            ))}

            <div
              style={{
                display: 'flex',
                gap: 'var(--sp-2)',
                marginTop: 'var(--sp-3)',
              }}
            >
              <Button variant="outline" size="sm" onClick={() => setShowDupWarning(false)}>
                Cancel
              </Button>
              <Button variant="gradient" size="sm" onClick={handleCreateAnyway}>
                Create anyway
              </Button>
            </div>
          </div>
        )}

        {/* Patient Name */}
        {fields.patient_name.enabled && !showDupWarning && (
          <div>
            <label style={labelStyle}>
              Patient Name {fields.patient_name.required && requiredMark}
            </label>
            <input
              type="text"
              placeholder="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                ...inputStyle,
                ...(errors.name ? { borderColor: '#dc2626' } : {}),
              }}
            />
            {errors.name && <div style={errorTextStyle}>{errors.name}</div>}
          </div>
        )}

        {/* Mobile Country Code + Number */}
        {(fields.mobile_country_code.enabled || fields.mobile_number.enabled) &&
          !showDupWarning && (
            <div>
              <label style={labelStyle}>
                Mobile Number {fields.mobile_number.required && requiredMark}
              </label>
              <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                {fields.mobile_country_code.enabled && (
                  <select
                    value={mobileCountryCode}
                    onChange={(e) => setMobileCountryCode(e.target.value)}
                    style={{
                      ...inputStyle,
                      width: 90,
                      cursor: 'pointer',
                      appearance: 'auto',
                      flexShrink: 0,
                    }}
                  >
                    <option value="+63">+63</option>
                    <option value="+1">+1</option>
                    <option value="+44">+44</option>
                    <option value="+61">+61</option>
                    <option value="+91">+91</option>
                    <option value="+81">+81</option>
                    <option value="+82">+82</option>
                  </select>
                )}
                {fields.mobile_number.enabled && (
                  <input
                    type="tel"
                    placeholder="Mobile number"
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    style={{
                      ...inputStyle,
                      flex: 1,
                      ...(errors.mobileNumber ? { borderColor: '#dc2626' } : {}),
                    }}
                  />
                )}
              </div>
              {errors.mobileNumber && <div style={errorTextStyle}>{errors.mobileNumber}</div>}
            </div>
          )}

        {/* Email */}
        {fields.email.enabled && !showDupWarning && (
          <div>
            <label style={labelStyle}>
              Email {fields.email.required ? requiredMark : '(optional)'}
            </label>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                ...inputStyle,
                ...(errors.email ? { borderColor: '#dc2626' } : {}),
              }}
            />
            {errors.email && <div style={errorTextStyle}>{errors.email}</div>}
          </div>
        )}

        {/* Date of Birth */}
        {fields.date_of_birth.enabled && !showDupWarning && (
          <div>
            <label style={labelStyle}>
              Date of Birth {fields.date_of_birth.required && requiredMark}
            </label>
            <input
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              style={{
                ...inputStyle,
                ...(errors.dateOfBirth ? { borderColor: '#dc2626' } : {}),
              }}
            />
            {errors.dateOfBirth && <div style={errorTextStyle}>{errors.dateOfBirth}</div>}
          </div>
        )}

        {/* Gender */}
        {fields.gender.enabled && !showDupWarning && (
          <div>
            <label style={labelStyle}>Gender {fields.gender.required && requiredMark}</label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              style={{
                ...inputStyle,
                cursor: 'pointer',
                appearance: 'auto',
              }}
            >
              <option value="">Select...</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
        )}
      </div>
    </Drawer>
  );
}
